/**
 * Service for managing voice trigger phrase settings
 */

const ENABLED_KEY = 'voice_trigger_enabled';
const WORD_KEY = 'voice_trigger_word';
const DEFAULT_TRIGGER_WORD = 'Aufnahme Start';

// Stop trigger constants
const STOP_ENABLED_KEY = 'stop_trigger_enabled';
const STOP_WORD_KEY = 'stop_trigger_word';
const DEFAULT_STOP_WORD = 'Aufnahme Stopp';

// Language constant
const LANGUAGE_KEY = 'trigger_language';
const DEFAULT_LANGUAGE = 'de-DE';

// Keyword variations for start triggers (German + English)
// Each phrase can match: exact phrase, word variations, or all words present
const START_KEYWORDS = {
  'aufnahme start': ['aufnahme start', 'aufnahme starten', 'start aufnahme', 'aufnahme', 'recording start', 'start recording'],
  aufnehmen: ['aufnehmen', 'aufnahme', 'record', 'recording', 'starten', 'start', 'los', 'beginnen', 'begin'],
};

// Keyword variations for stop triggers (German + English)
const STOP_KEYWORDS = {
  'aufnahme stopp': ['aufnahme stopp', 'aufnahme stop', 'stopp aufnahme', 'stop aufnahme', 'aufnahme ende', 'recording stop', 'stop recording'],
  stop: ['stop', 'stopp', 'ende', 'fertig', 'aufhören', 'end', 'finish'],
};

export const voiceTriggerService = {
  /**
   * Check if voice trigger is enabled
   * @returns {boolean}
   */
  isEnabled: () => {
    try {
      const stored = localStorage.getItem(ENABLED_KEY);
      // Default to true for backwards compatibility
      return stored === null ? true : stored === 'true';
    } catch (error) {
      console.error('Error reading voice trigger setting:', error);
      return true;
    }
  },

  /**
   * Enable voice trigger
   */
  enable: () => {
    try {
      localStorage.setItem(ENABLED_KEY, 'true');
    } catch (error) {
      console.error('Error saving voice trigger setting:', error);
    }
  },

  /**
   * Disable voice trigger
   */
  disable: () => {
    try {
      localStorage.setItem(ENABLED_KEY, 'false');
    } catch (error) {
      console.error('Error saving voice trigger setting:', error);
    }
  },

  /**
   * Get the configured trigger word
   * @returns {string}
   */
  getTriggerWord: () => {
    try {
      const stored = localStorage.getItem(WORD_KEY);
      return stored || DEFAULT_TRIGGER_WORD;
    } catch (error) {
      console.error('Error reading trigger word:', error);
      return DEFAULT_TRIGGER_WORD;
    }
  },

  /**
   * Set the trigger word
   * @param {string} word - The trigger word to set
   */
  setTriggerWord: (word) => {
    try {
      localStorage.setItem(WORD_KEY, word.toLowerCase().trim());
    } catch (error) {
      console.error('Error saving trigger word:', error);
    }
  },

  /**
   * Get all trigger keywords based on the configured word
   * Returns variations for German + English language support
   * @returns {string[]}
   */
  getTriggerKeywords: () => {
    const word = voiceTriggerService.getTriggerWord().toLowerCase();
    return START_KEYWORDS[word] || [word];
  },

  /**
   * Get default trigger word
   * @returns {string}
   */
  getDefaultWord: () => DEFAULT_TRIGGER_WORD,

  // ==================== STOP TRIGGER ====================

  /**
   * Check if stop trigger is enabled
   * @returns {boolean}
   */
  isStopEnabled: () => {
    try {
      const stored = localStorage.getItem(STOP_ENABLED_KEY);
      return stored === 'true';
    } catch (error) {
      console.error('Error reading stop trigger setting:', error);
      return false;
    }
  },

  /**
   * Enable stop trigger
   */
  enableStop: () => {
    try {
      localStorage.setItem(STOP_ENABLED_KEY, 'true');
    } catch (error) {
      console.error('Error saving stop trigger setting:', error);
    }
  },

  /**
   * Disable stop trigger
   */
  disableStop: () => {
    try {
      localStorage.setItem(STOP_ENABLED_KEY, 'false');
    } catch (error) {
      console.error('Error saving stop trigger setting:', error);
    }
  },

  /**
   * Get the configured stop word
   * @returns {string}
   */
  getStopWord: () => {
    try {
      const stored = localStorage.getItem(STOP_WORD_KEY);
      return stored || DEFAULT_STOP_WORD;
    } catch (error) {
      console.error('Error reading stop word:', error);
      return DEFAULT_STOP_WORD;
    }
  },

  /**
   * Set the stop word
   * @param {string} word - The stop word to set
   */
  setStopWord: (word) => {
    try {
      localStorage.setItem(STOP_WORD_KEY, word.toLowerCase().trim());
    } catch (error) {
      console.error('Error saving stop word:', error);
    }
  },

  /**
   * Get all stop keywords based on the configured word
   * Returns variations for German + English language support
   * @returns {string[]}
   */
  getStopKeywords: () => {
    const word = voiceTriggerService.getStopWord().toLowerCase();
    return STOP_KEYWORDS[word] || [word];
  },

  /**
   * Get default stop word
   * @returns {string}
   */
  getDefaultStopWord: () => DEFAULT_STOP_WORD,

  // ==================== LANGUAGE ====================

  /**
   * Get the configured trigger language (for speech recognition)
   * @returns {string} Language code (e.g., 'de-DE', 'en-US')
   */
  getLanguage: () => {
    try {
      const stored = localStorage.getItem(LANGUAGE_KEY);
      return stored || DEFAULT_LANGUAGE;
    } catch (error) {
      console.error('Error reading trigger language:', error);
      return DEFAULT_LANGUAGE;
    }
  },

  /**
   * Set the trigger language
   * @param {string} lang - Language code (e.g., 'de-DE', 'en-US')
   */
  setLanguage: (lang) => {
    try {
      localStorage.setItem(LANGUAGE_KEY, lang);
    } catch (error) {
      console.error('Error saving trigger language:', error);
    }
  },

  /**
   * Get supported languages for speech recognition
   * @returns {Array<{code: string, label: string}>}
   */
  getSupportedLanguages: () => [
    { code: 'de-DE', label: 'Deutsch' },
    { code: 'en-US', label: 'English (US)' },
    { code: 'en-GB', label: 'English (UK)' },
  ],

  /**
   * Get default language
   * @returns {string}
   */
  getDefaultLanguage: () => DEFAULT_LANGUAGE,

  // ==================== PHRASE DURATION ====================

  /**
   * Estimate the duration of a phrase in milliseconds.
   * Based on average speaking rate of ~2.5 words/second (150 wpm).
   * @param {string} phrase - The phrase to estimate
   * @returns {number} Estimated duration in ms
   */
  estimatePhraseDurationMs: (phrase) => {
    const words = phrase.trim().split(/\s+/).length;
    const MS_PER_WORD = 400; // ~2.5 words/second
    const RECOGNITION_LATENCY_MS = 300;
    return RECOGNITION_LATENCY_MS + (words * MS_PER_WORD);
  },

  /**
   * Get estimated duration of the configured start trigger phrase
   * @returns {number} Duration in ms to trim from start of recording
   */
  getStartPhraseTrimDurationMs: () => {
    const phrase = voiceTriggerService.getTriggerWord();
    return voiceTriggerService.estimatePhraseDurationMs(phrase);
  },

  /**
   * Get estimated duration of the configured stop trigger phrase
   * @returns {number} Duration in ms to trim from end of recording
   */
  getStopPhraseTrimDurationMs: () => {
    const phrase = voiceTriggerService.getStopWord();
    return voiceTriggerService.estimatePhraseDurationMs(phrase);
  },

  // ==================== BACKEND SYNC ====================

  /**
   * Sync trigger settings from backend user settings.
   * If backend has values set, they override localStorage.
   * @param {Object} settings - User settings object from backend
   */
  syncFromBackend: (settings) => {
    if (!settings) return;

    // Sync enabled state
    if (typeof settings.voiceTriggerEnabled === 'boolean') {
      console.log('Syncing voice trigger enabled from backend:', settings.voiceTriggerEnabled);
      localStorage.setItem('voice_trigger_enabled', settings.voiceTriggerEnabled.toString());
      // Also sync stop trigger to match (they're linked)
      localStorage.setItem('stop_trigger_enabled', settings.voiceTriggerEnabled.toString());
    }

    if (settings.voiceTriggerStartPhrase) {
      console.log('Syncing start phrase from backend:', settings.voiceTriggerStartPhrase);
      localStorage.setItem(WORD_KEY, settings.voiceTriggerStartPhrase.toLowerCase().trim());
    }

    if (settings.voiceTriggerStopPhrase) {
      console.log('Syncing stop phrase from backend:', settings.voiceTriggerStopPhrase);
      localStorage.setItem(STOP_WORD_KEY, settings.voiceTriggerStopPhrase.toLowerCase().trim());
    }
  },
};
